import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import _get from 'lodash/get';
import { Attachment } from 'graphql-common';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import getDocumentationFilePermissions from '@lib/utils/documentation/getDocumentationFilePermissions';
import DocumentVersionNotes from '@lib/components/DocumentationPreview/DocumentVersionNotes';
import Trans from '@lib/components/Trans/Trans';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import {
  GetUrlParams,
  ORDERING_DIRECTION_DESC,
  OrderingParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import { IconName } from '@lib/components/Modal/enums';
import { APP_URLS } from 'constants/urls';
import getTopDocumentsTableColumns from './utils/getTopDocumentsTableColumns';
import getTopDocumentsActionsMenu from './utils/getTopDocumentsActionsMenu';
import useTopDocumentsData from './useTopDocumentsData';
import useTopDocumentsActions from './useTopDocumentsActions';
import TableEmptyState from './TableEmptyState';
import styles from './TopDocumentsByViewsTable.module.scss';

interface Props {
  period: string[];
  isDefaultPeriod: boolean;
  onClearPeriod: () => void;
  // Reports the first successful data load up to the page-level loader.
  onFirstLoaded?: () => void;
}

const PER_PAGE_LIMIT = 10;

const defaultPaginationParams: PaginationParams = {
  ...PAGINATION_PARAMS,
};

const defaultOrderingParams: OrderingParams = {
  [GetUrlParams.OrderingField]: 'viewCount',
  [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_DESC,
};

export default function TopDocumentsByViewsTable({
  period,
  isDefaultPeriod,
  onClearPeriod,
  onFirstLoaded,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const paginationState = useState<PaginationParams>(defaultPaginationParams);
  const orderingState = useState<OrderingParams>(defaultOrderingParams);
  const [paginationParams, setPaginationParams] = paginationState;
  const [orderingParams] = orderingState;

  const { rows, totalCount, loading, refetchAnalytics } = useTopDocumentsData({
    period,
    searchQuery,
    paginationParams,
    orderingParams,
    perPageDefault: PER_PAGE_LIMIT,
  });

  // Fire `onFirstLoaded` once when both queries (analytics + documentations
  // join) have settled — that's the moment the row payload is fully usable.
  const firstLoadReportedRef = useRef(false);
  useEffect(() => {
    if (!loading && !firstLoadReportedRef.current) {
      firstLoadReportedRef.current = true;
      onFirstLoaded?.();
    }
  }, [loading, onFirstLoaded]);

  const {
    fileViewerDocument,
    versionNotesDocument,
    deleteIds,
    onOpenFile,
    onViewDocument,
    onVersionNotes,
    onDelete,
    onConfirmedDelete,
    closeDeleteModal,
    closeFileViewer,
    closeVersionNotes,
  } = useTopDocumentsActions({ t, onDeleted: refetchAnalytics });

  const fileViewerPermissions =
    getDocumentationFilePermissions(fileViewerDocument);

  const tableColumns = useMemo(
    () => getTopDocumentsTableColumns({ onOpen: onOpenFile, t }),
    [onOpenFile, t],
  );

  const tableActionsMenu = useMemo(
    () =>
      getTopDocumentsActionsMenu({
        onViewDocument,
        onVersionNotes,
        onDetails: (item) =>
          navigate(
            APP_URLS.dashboard.documentation.view.getDynamicPath({
              pathParts: { id: item.id },
            }),
          ),
        onEdit: (item) =>
          navigate(
            APP_URLS.dashboard.documentation.edit.getDynamicPath({
              pathParts: { id: item.id },
            }),
          ),
        onDelete,
        t,
      }),
    [navigate, onDelete, onVersionNotes, onViewDocument, t],
  );

  // Reset pagination to page 1 whenever the search query changes.
  const onSearchChange = useCallback(
    (v: string) => {
      setSearchQuery(v);
      setPaginationParams({ ...paginationParams, [GetUrlParams.Page]: '1' });
    },
    [paginationParams, setPaginationParams],
  );
  const clearSearch = useCallback(() => onSearchChange(''), [onSearchChange]);

  const showSearch = !!(totalCount || searchQuery);

  return (
    <>
      <PageHeader title={t('top-documents-by-views')} />
      <PageBody>
        <div className={styles.root}>
          {showSearch && (
            <div className={styles.searchWrap}>
              <Search
                placeholder={t('documents-search-placeholder-premium')}
                onChange={onSearchChange}
                useSearchParam={false}
                useBottomMargin={false}
              />
            </div>
          )}
          <Table
            columns={tableColumns}
            data={rows}
            actionsMenu={tableActionsMenu}
            id="top-documents-by-views"
            loading={loading}
            orderingState={orderingState}
            paginationState={paginationState}
            totalEntries={totalCount}
            emptyState={
              <TableEmptyState
                searchQuery={searchQuery}
                isDefaultPeriod={isDefaultPeriod}
                onClearSearch={clearSearch}
                onClearPeriod={onClearPeriod}
              />
            }
          />
        </div>
      </PageBody>
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="documents-bulk-delete-text" />}
        icon={IconName.Delete}
        isOpen={deleteIds.length > 0}
        onClose={closeDeleteModal}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
        useProgress
      />
      <FileViewerModal
        attachment={
          _get(fileViewerDocument, [
            'currentVersion',
            'attachmentAttached',
          ]) as Attachment
        }
        isOpen={!!fileViewerDocument}
        documentationVersionId={fileViewerDocument?.currentVersion?.id}
        onClose={closeFileViewer}
        useDownloadTool={fileViewerPermissions.allowDownload}
        usePrintTool={fileViewerPermissions.allowPrint}
      />
      <DocumentVersionNotes
        isOpen={!!versionNotesDocument}
        notes={_get(versionNotesDocument, ['currentVersion', 'notes']) || ''}
        onClose={closeVersionNotes}
      />
    </>
  );
}
